// UPI API (spec §2, §8): resolve a UPI ID or mobile, UPI PIN set-up and
// verification.
import type { UpiResolution } from '@/types/phase2.types';
import { ApiError, get, post } from './client';
import { MOCK_UPI, REAL_API_ENABLED } from './flags';
import * as mock from '@/mock/handlers/upi';

const notAvailable = (): never => {
  throw new ApiError('NOT_AVAILABLE', 'The UPI API is not available.');
};

export interface UpiHandle {
  id?: string;
  vpa?: string;
  upiId?: string;
  isPrimary?: boolean;
  status?: string;
  [key: string]: unknown;
}

export interface UpiPaymentInput {
  debitAccountNumber: string;
  payeeVpa: string;
  payeeAccountNumber?: string;
  amountPaise: number;
  remarks?: string;
  upiPin: string;
  payerVpa?: string;
}

export interface VerifyUpiPinInput {
  vpa: string;
  pin: string;
}

export interface SetUpiPinInput {
  vpa: string;
  debitAccountNumber: string;
  cardLast6: string;
  cardExpiryMmYy: string;
  otpTransactionId: string;
  code: string;
  pin: string;
}

export type ResetUpiPinInput = SetUpiPinInput;

export interface ChangeUpiPinInput {
  vpa: string;
  oldPin: string;
  newPin: string;
}

export interface UpiRemittanceInput {
  [key: string]: unknown;
}

export interface UpiCollectInput {
  [key: string]: unknown;
}

export const createUpiCollectRequest = (input: UpiCollectInput): Promise<Record<string, unknown>> =>
  MOCK_UPI
    ? Promise.reject(new ApiError('MOCK_ONLY', 'UPI collect is handled by the mock flow.'))
    : post<Record<string, unknown>>('/upi/collect', input);

export const listUpiHandles = async (): Promise<UpiHandle[]> => {
  if (MOCK_UPI) return [];
  const data = await get<unknown>('/upi/handles');
  const rows = Array.isArray(data)
    ? data
    : (data as { handles?: unknown[]; items?: unknown[] }).handles
      ?? (data as { items?: unknown[] }).items
      ?? [];
  return rows.filter((row): row is UpiHandle => Boolean(row && typeof row === 'object'));
};

export const payUpi = async (input: UpiPaymentInput, idempotencyKey: string): Promise<Record<string, unknown>> => {
  if (MOCK_UPI) throw new ApiError('MOCK_ONLY', 'UPI payment is handled by the payment mock.');
  return getUpiPayment('/upi/pay', input, idempotencyKey);
};

export const sendUpiRemittance = async (input: UpiRemittanceInput, idempotencyKey: string): Promise<Record<string, unknown>> => {
  if (MOCK_UPI) throw new ApiError('MOCK_ONLY', 'UPI remittance is handled by the payment mock.');
  return getUpiPayment('/upi/remittance', input, idempotencyKey);
};

const getUpiPayment = async (
  path: '/upi/pay' | '/upi/remittance',
  body: unknown,
  idempotencyKey: string,
): Promise<Record<string, unknown>> => {
  return post<Record<string, unknown>>(path, body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
};

export const resolveUpiIdOrMobile = (
  input: { kind: 'UPI_ID'; upiId: string } | { kind: 'MOBILE'; mobile: string },
  qrName?: string
): Promise<UpiResolution> => (MOCK_UPI ? mock.resolveUpiIdOrMobile(input, qrName) : notAvailable());

export const hasUpiPin = async (): Promise<boolean> => {
  if (MOCK_UPI) return mock.hasUpiPin();
  if (!REAL_API_ENABLED) return notAvailable();
  const data = await get<Record<string, unknown>>('/payments/context');
  return Boolean(data.upiPinSet ?? data.upiPinConfigured ?? data.upiEnabled ?? true);
};

export const setUpiPin = async (input: SetUpiPinInput): Promise<Record<string, unknown>> => {
  if (MOCK_UPI) {
    await mock.setUpiPin(input.pin);
    return {};
  }
  if (!REAL_API_ENABLED) return notAvailable();
  return post<Record<string, unknown>>('/upi/pin/set', input);
};

export const setMockUpiPin = async (pin: string): Promise<void> => {
  if (!MOCK_UPI) {
    throw new ApiError('UPI_PIN_SETUP_DETAILS_REQUIRED', 'UPI PIN setup requires debit-card details and an OTP.');
  }
  await mock.setUpiPin(pin);
};

export const resetUpiPin = async (input: ResetUpiPinInput): Promise<Record<string, unknown>> => {
  if (MOCK_UPI) {
    await mock.setUpiPin(input.pin);
    return {};
  }
  if (!REAL_API_ENABLED) return notAvailable();
  return post<Record<string, unknown>>('/upi/pin/reset', input);
};

export const changeUpiPin = async (input: ChangeUpiPinInput): Promise<Record<string, unknown>> => {
  if (MOCK_UPI) {
    await mock.setUpiPin(input.newPin);
    return {};
  }
  if (!REAL_API_ENABLED) return notAvailable();
  return post<Record<string, unknown>>('/upi/pin/change', input);
};

export const verifyUpiPin = async (input: VerifyUpiPinInput): Promise<{ authTicket: string }> => {
  if (MOCK_UPI) return mock.verifyUpiPin(input.pin);
  if (!REAL_API_ENABLED) return notAvailable();
  try {
    const result = await post<{ authTicket?: string; token?: string }>('/upi/pin/verify', input);
    const authTicket = result.authTicket ?? result.token;
    if (!authTicket) {
      throw new ApiError('UPI_PIN_RESPONSE_INVALID', 'UPI PIN verification response was invalid.');
    }
    return { authTicket };
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.code === 'PIN_005') throw new ApiError('PIN_005', 'Your UPI PIN is not set. Set it to continue.');
      if (error.code === 'PIN_006') throw new ApiError('PIN_006', 'That UPI PIN is incorrect. Try again.');
      if (error.code === 'PIN_007') throw new ApiError('PIN_007', 'Your UPI PIN is locked. Try again later.');
    }
    throw error;
  }
};
